import { useNavigate, useSearch } from "@tanstack/react-router";
import { usePurchaseOrders } from "@/api/queries";
import type { PurchaseOrderSummary } from "@/api/types";
import { KpiStrip } from "@/components/KpiStrip";
import type { Column } from "@/components/list/DataTable";
import type { ListSearch } from "@/components/list/listQuery";
import { ListScreen, listQueryFor } from "@/components/list/ListScreen";
import { PermittedAction } from "@/components/PermittedAction";
import { formatDate } from "@/lib/dates";
import { formatAmount } from "@/lib/money";
import { PurchaseOrderStatusBadge } from "./PurchaseOrderStatusBadge";

const STATUSES = [
  { value: "all", label: "All statuses" },
  { value: "draft", label: "Draft" },
  { value: "sent", label: "Sent" },
  { value: "confirmed", label: "Confirmed" },
  { value: "partiallyReceived", label: "Partially received" },
  { value: "received", label: "Received" },
  { value: "billed", label: "Billed" },
  { value: "cancelled", label: "Cancelled" },
];

export function PurchaseOrdersListPage() {
  const search = useSearch({ from: "/purchases/orders" }) as ListSearch;
  const navigate = useNavigate();
  const { data, isPending, error, refetch } = usePurchaseOrders(listQueryFor(search));
  const overview = data?.overview;

  const columns: Array<Column<PurchaseOrderSummary>> = [
    {
      key: "number",
      header: "Number",
      width: "12ch",
      numeric: true,
      render: (row) => row.number ?? <span className="text-fg-hint">Not yet sent</span>,
    },
    { key: "supplier", header: "Supplier", render: (row) => row.supplierName },
    { key: "date", header: "Date", width: "12ch", render: (row) => formatDate(row.date) },
    {
      key: "expected",
      header: "Expected",
      width: "12ch",
      render: (row) => formatDate(row.expected),
    },
    {
      key: "received",
      header: "Received",
      width: "13ch",
      render: (row) =>
        row.status === "draft" || row.status === "cancelled"
          ? "—"
          : `${row.linesReceived} of ${row.lineCount} lines`,
    },
    {
      key: "status",
      header: "Status",
      width: "17ch",
      render: (row) => <PurchaseOrderStatusBadge status={row.status} />,
    },
    {
      key: "total",
      header: "Amount",
      align: "right",
      numeric: true,
      render: (row) => formatAmount(row.total),
    },
  ];

  return (
    <ListScreen
      route="/purchases/orders"
      title="Purchase orders"
      subtitle={
        overview
          ? `${overview.onOrderCount} on order · ${overview.toBillCount} to bill`
          : "Loading purchase orders"
      }
      actions={
        <PermittedAction
          permission="purchases.orders.create"
          leftIcon="Plus"
          onClick={() => void navigate({ to: "/purchases/orders/new" })}
        >
          New purchase order
        </PermittedAction>
      }
      summary={
        overview ? (
          <KpiStrip
            items={[
              {
                label: "On order",
                value: formatAmount(overview.onOrderAmount),
                detail: `${overview.onOrderCount} open with suppliers`,
                to: "/purchases/orders",
                search: { status: "sent" },
              },
              {
                label: "Received, not yet billed",
                value: formatAmount(overview.toBillAmount),
                detail: `${overview.toBillCount} ready to convert`,
                to: "/purchases/orders",
                search: { status: "received" },
              },
              {
                label: "Overdue delivery",
                value: String(overview.overdueCount),
                detail: "past the expected date",
                tone: overview.overdueCount > 0 ? "warn" : "default",
                to: "/purchases/orders",
              },
            ]}
          />
        ) : null
      }
      statuses={STATUSES}
      columns={columns}
      rows={data?.items ?? []}
      total={data?.total ?? 0}
      hasNext={data?.next != null}
      loading={isPending}
      error={error}
      onRetry={() => void refetch()}
      rowKey={(row) => row.id}
      rowLabel={(row) =>
        `Purchase order ${row.number ?? "not yet sent"}, ${row.supplierName}, ${formatAmount(row.total)}`
      }
      onRowClick={(row) => void navigate({ to: "/purchases/orders/$id", params: { id: row.id } })}
      searchPlaceholder="Search number, supplier or reference"
      empty={{
        icon: "ClipboardList",
        title: "No purchase orders yet",
        description:
          "An order records what you've asked a supplier for. It posts nothing until it's billed.",
      }}
      filteredEmpty={{
        icon: "Search",
        title: "No purchase orders match these filters",
        description: "Try a wider date range, or clear the filters to see everything again.",
      }}
    />
  );
}
